import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDialog } from "@shared/context/AppDialogContext";
import { useSettingsSafe } from "@shared/contexts/SettingsContext";

const DEFAULT_MESSAGE =
  "You have unsaved changes. Leave this page and discard them?";

/**
 * Warns on tab close/refresh and in-app link navigation when `isDirty` is true.
 * Works with BrowserRouter (no data-router required).
 */
export function useUnsavedChangesWarning(
  isDirty: boolean,
  message: string = DEFAULT_MESSAGE,
): void {
  const dialog = useAppDialog();
  const navigate = useNavigate();
  const { settings } = useSettingsSafe();
  const confirmNavigation = settings.confirm_navigation !== false;
  const dirtyRef = useRef(isDirty);
  const pendingHrefRef = useRef<string | null>(null);
  const confirmingRef = useRef(false);

  dirtyRef.current = isDirty;

  useEffect(() => {
    if (!isDirty || !confirmNavigation || typeof window === "undefined") {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [confirmNavigation, isDirty, message]);

  useEffect(() => {
    if (!isDirty || !confirmNavigation || typeof document === "undefined") {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (!dirtyRef.current || confirmingRef.current) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchor = (event.target as HTMLElement | null)?.closest(
        "a[href]",
      ) as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:")) return;
      if (anchor.target && anchor.target !== "_self") return;

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      pendingHrefRef.current = `${url.pathname}${url.search}${url.hash}`;
      confirmingRef.current = true;

      void dialog
        .confirm(message)
        .then((confirmed) => {
          const next = pendingHrefRef.current;
          pendingHrefRef.current = null;
          confirmingRef.current = false;
          if (confirmed && next) {
            dirtyRef.current = false;
            navigate(next);
          }
        })
        .catch(() => {
          pendingHrefRef.current = null;
          confirmingRef.current = false;
        });
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () =>
      document.removeEventListener("click", handleDocumentClick, true);
  }, [confirmNavigation, dialog, isDirty, message, navigate]);
}
